import { Box, Typography, Chip, Stack, Skeleton } from '@mui/material';
import { Button } from '@uy3/web-components';
import { formatCustomDate, maskCurrencyInCents } from 'helpers';
import React from 'react';

type Installment = {
    valorVencimento: number;
    codigoVencimentoDisplay: string;
};

interface SrcDetailsProps {
    title: string;
    totalAmount: number;
    installments: Installment[];
    isLoading?: boolean;
}
interface ActionButtonsProps {
    lastUpdate?: string;
    onViewOriginalData?: () => void;
    onConsultAgain?: () => void;
    disableRequestDataSet?: boolean;
}

export const contentStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    maxWidth: '100%'
};

export const ScrDetailsSkeleton: React.FC = () => (
    <Box
        sx={{
            width: '100%',
            backgroundColor: '#F4F3F3',
            borderRadius: 2,
            p: 4,
            mt: 2,
            minHeight: 180
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2
            }}
        >
            <Skeleton variant="text" width={220} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton
                variant="rectangular"
                width={120}
                height={36}
                sx={{ borderRadius: 2, ml: 'auto' }}
            />
        </Box>
        <Stack direction="row" spacing={1} sx={{ pb: 1 }}>
            {[...Array(5)].map((_, idx) => (
                <Box key={idx} sx={{ minWidth: 120, p: 1 }}>
                    <Skeleton
                        variant="text"
                        width={80}
                        height={28}
                        sx={{ mb: 1, borderRadius: 1 }}
                    />
                    <Skeleton variant="text" width={100} height={20} sx={{ borderRadius: 1 }} />
                </Box>
            ))}
        </Stack>
    </Box>
);

export const ActionButtons: React.FC<ActionButtonsProps> = ({
    lastUpdate,
    onViewOriginalData,
    onConsultAgain,
    disableRequestDataSet = false
}) => (
    <Box
        sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'end',
            backgroundColor: '#F4F3F3',
            borderRadius: 2,
            height: 64,
            gap: 5,
            p: 1.5
        }}
    >
        <Typography
            variant="body2"
            color="neutral.500"
            sx={{
                fontFamily: 'Lato, sans-serif',
                fontWeight: 400,
                fontSize: 14,
                alignSelf: 'center'
            }}
        >
            Última atualização: {formatCustomDate(lastUpdate as string)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
                size="medium"
                sx={{
                    height: 40,
                    borderRadius: 2
                }}
                variant="outlined"
                disabled={disableRequestDataSet}
                onClick={onViewOriginalData}
            >
                Ver dados originais
            </Button>
            <Button
                size="medium"
                sx={{
                    height: 40,
                    borderRadius: 2
                }}
                variant="contained"
                disabled={disableRequestDataSet}
                onClick={onConsultAgain}
            >
                Consultar novamente
            </Button>
        </Box>
    </Box>
);

const InstallmentCard: React.FC<Installment> = ({ valorVencimento, codigoVencimentoDisplay }) => {
    return (
        <Box
            sx={{
                minWidth: 120,
                p: 1,
                textAlign: 'left'
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#212529',
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: '120%',
                    letterSpacing: '-0.2px',
                    mb: 0.5
                }}
            >
                {maskCurrencyInCents(valorVencimento)}
            </Typography>
            <Typography
                sx={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#6C757D',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: '120%',
                    letterSpacing: '-0.2px'
                }}
            >
                {codigoVencimentoDisplay}
            </Typography>
        </Box>
    );
};

export const ScrDetails: React.FC<SrcDetailsProps> = ({
    title,
    totalAmount,
    installments,
    isLoading = false
}) => {
    return (
        <>
            {isLoading ? (
                <ScrDetailsSkeleton />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        backgroundColor: '#F4F3F3',
                        borderRadius: 2,
                        p: 4,
                        mt: 2,
                        minHeight: 180
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Lato, sans-serif',
                                fontWeight: 400,
                                fontSize: 24,
                                lineHeight: '120%',
                                letterSpacing: '-0.2px',
                                color: '#212529'
                            }}
                        >
                            {title}
                        </Typography>
                        <Chip
                            label={`Total: ${maskCurrencyInCents(totalAmount)}`}
                            size="small"
                            sx={{
                                backgroundColor: '#fff',
                                color: '#212529',
                                fontSize: 14,
                                fontWeight: 600,
                                fontFamily: 'Lato, sans-serif',
                                lineHeight: '120%',
                                border: '1px solid #000000',
                                letterSpacing: '-0.2px',
                                ml: 'auto',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                height: 'auto',
                                minHeight: 40,
                                '& .MuiChip-label': {
                                    px: 1.5,
                                    py: 1,
                                    fontFamily: 'Lato, sans-serif',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    lineHeight: '120%',
                                    letterSpacing: '-0.2px'
                                }
                            }}
                        />
                    </Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            overflowX: 'auto',
                            pb: 1,
                            '&::-webkit-scrollbar': {
                                height: 4
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#888',
                                borderRadius: 4
                            }
                        }}
                    >
                        {installments?.map((installment, idx) => (
                            <InstallmentCard key={idx} {...installment} />
                        ))}
                    </Stack>
                </Box>
            )}
        </>
    );
};
